import { apiService } from '@/api/apiService'
import { fetchingAPI } from '@/api/fetchingAPI'
import { toast } from '@/components/toasts/toast.helpers'

import { setData, setLoading } from './bulkScoring.redux'

const MAX_SSE_BUFFER_SIZE = 1024 * 1024

const JOB_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
}

const getAuthHeaders = (currentUser) => ({
  'Content-Type': 'application/json',
  'auth-type': 'web',
  username: currentUser.username,
  organizationid: String(currentUser.organizationid),
  token: currentUser.token,
})

const readSSEStream = async (response, { onEvent }) => {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let done = false

  while (!done) {
    const { done: streamDone, value } = await reader.read()
    done = streamDone
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })

    if (buffer.length > MAX_SSE_BUFFER_SIZE) {
      throw new Error('SSE buffer exceeded maximum size')
    }

    const sseMessages = buffer.split('\n\n')
    buffer = sseMessages.pop()

    for (const sseMessage of sseMessages) {
      for (const line of sseMessage.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))
            onEvent(data)
          } catch {
            // malformed SSE event
          }
        }
      }
    }
  }
}

export const fetchScorecardConfig = (configId) => async (dispatch) => {
  dispatch(setLoading('scorecardConfig', true))
  dispatch(setData('scorecardConfig', null))

  try {
    const config = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${configId}`,
      'GET',
      dispatch
    )
    dispatch(setData('scorecardConfig', config))
  } catch (error) {
    toast.error('Failed to fetch scorecard config details', { cause: error })
  } finally {
    dispatch(setLoading('scorecardConfig', false))
  }
}

export const previewBulkScore = (params) => async (dispatch) => {
  dispatch(setLoading('scorePreview', true))
  dispatch(setData('scorePreview', null))

  try {
    const result = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/bulk-score/preview`,
      'POST',
      dispatch,
      JSON.stringify(params)
    )
    dispatch(setData('scorePreview', result))
  } catch (error) {
    toast.error(error?.detail || error?.message || 'Failed to fetch preview')
  } finally {
    dispatch(setLoading('scorePreview', false))
  }
}

export const previewBulkRescore = (params) => async (dispatch) => {
  dispatch(setLoading('rescorePreview', true))
  dispatch(setData('rescorePreview', null))

  try {
    const result = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/bulk-rescore-criteria/preview`,
      'POST',
      dispatch,
      JSON.stringify(params)
    )
    dispatch(setData('rescorePreview', result))
  } catch (error) {
    toast.error(error?.message || 'Failed to fetch preview')
  } finally {
    dispatch(setLoading('rescorePreview', false))
  }
}

export const submitBulkScore =
  (params, { abortController, onProgress }) =>
  async (dispatch, getState) => {
    dispatch(setLoading('scoreSubmitting', true))
    dispatch(setData('scoreJobStatus', null))
    dispatch(setData('scoreJobId', null))

    const { currentUser } = getState()

    try {
      const response = await fetch(`${apiService.scorecard}/scoring/scores/bulk-score`, {
        method: 'POST',
        headers: getAuthHeaders(currentUser),
        body: JSON.stringify(params),
        signal: abortController.signal,
        redirect: 'error',
      })

      if (!response.ok) {
        let responseError
        try {
          responseError = await response.json()
        } catch {
          toast.error(`Server returned ${response.status}`)
          return
        }
        if (response.status === 409 && responseError?.existing_job) {
          const existingJob = responseError.existing_job
          dispatch(setData('scoreJobId', existingJob.job_id))
          dispatch(setData('scoreJobStatus', existingJob))
          dispatch(setData('scorePreview', null))
          toast.warning(
            `A job is already running for this config (${existingJob.processed}/${existingJob.total_items} processed)`
          )
        } else {
          toast.error(responseError?.detail || 'Failed to start bulk score')
        }
        return
      }

      dispatch(setData('scorePreview', null))

      let currentJobId = null

      await readSSEStream(response, {
        onEvent: (data) => {
          onProgress(data)

          if (!currentJobId) {
            currentJobId = data.job_id
            dispatch(setData('scoreJobId', data.job_id))
            toast.success(`Bulk score job started: ${data.job_id}`)
          }

          if (data.status === JOB_STATUS.COMPLETED) {
            toast.success(
              `Bulk score completed: ${data.succeeded} succeeded, ${data.failed} failed`
            )
          } else if (data.status === JOB_STATUS.FAILED) {
            toast.error('Bulk score job failed')
          } else if (data.status === JOB_STATUS.CANCELLED) {
            toast.info('Bulk score job cancelled')
          }
        },
      })
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to start bulk score', { cause: error })
      }
    } finally {
      dispatch(setLoading('scoreSubmitting', false))
    }
  }

export const submitBulkRescore =
  (params, { abortController, onProgress }) =>
  async (dispatch, getState) => {
    dispatch(setLoading('rescoreSubmitting', true))
    dispatch(setData('rescoreJobStatus', null))
    dispatch(setData('rescoreJobId', null))

    const { currentUser } = getState()

    try {
      const response = await fetch(`${apiService.scorecard}/scoring/scores/bulk-rescore-criteria`, {
        method: 'POST',
        headers: getAuthHeaders(currentUser),
        body: JSON.stringify(params),
        signal: abortController.signal,
        redirect: 'error',
      })

      if (!response.ok) {
        let responseError
        try {
          responseError = await response.json()
        } catch {
          toast.error(`Server returned ${response.status}`)
          return
        }
        if (response.status === 409 && responseError?.existing_job) {
          const existingJob = responseError.existing_job
          dispatch(setData('rescoreJobId', existingJob.job_id))
          dispatch(setData('rescoreJobStatus', existingJob))
          dispatch(setData('rescorePreview', null))
          toast.warning(
            `A job is already running with overlapping criteria (${existingJob.processed}/${existingJob.total_items} processed)`
          )
        } else {
          toast.error(responseError?.detail || 'Failed to start bulk rescore')
        }
        return
      }

      dispatch(setData('rescorePreview', null))

      let currentJobId = null

      await readSSEStream(response, {
        onEvent: (data) => {
          onProgress(data)

          if (!currentJobId) {
            currentJobId = data.job_id
            dispatch(setData('rescoreJobId', data.job_id))
            toast.success(`Bulk rescore job started: ${data.job_id}`)
          }

          if (data.status === JOB_STATUS.COMPLETED) {
            toast.success(
              `Bulk rescore completed: ${data.succeeded} succeeded, ${data.failed} failed`
            )
          } else if (data.status === JOB_STATUS.FAILED) {
            toast.error('Bulk rescore job failed')
          } else if (data.status === JOB_STATUS.CANCELLED) {
            toast.info('Bulk rescore job cancelled')
          }
        },
      })
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to start bulk rescore', { cause: error })
      }
    } finally {
      dispatch(setLoading('rescoreSubmitting', false))
    }
  }
